import { useState, useRef, useEffect, forwardRef } from "react";
import { Check, ChevronsUpDown, Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DataverseEntityInfo } from "@shared/api-types";

interface EntityComboboxProps {
  entities: DataverseEntityInfo[] | undefined;
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
}

export const EntityCombobox = forwardRef<HTMLButtonElement, EntityComboboxProps>(({
  entities,
  value,
  onChange,
  isLoading,
  disabled = false,
  placeholder = "Select an entity",
  name
}, ref) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Keep track of whether a selection was made
  const selectionMadeRef = useRef(false);
  
  // When the popover closes, reset the search if no selection was made
  useEffect(() => {
    if (!open && !selectionMadeRef.current) {
      setSearchQuery("");
    }
    if (open) {
      selectionMadeRef.current = false;
    }
  }, [open]);
  
  const handleSelect = (currentValue: string) => {
    selectionMadeRef.current = true;
    onChange(currentValue === value ? "" : currentValue);
    setOpen(false);
  };
  
  // Filter entities based on search query
  const filteredEntities = entities?.filter((entity) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (entity.LogicalName ?? "").toLowerCase().includes(query) ||
      (entity.SchemaName ?? "").toLowerCase().includes(query)
    );
  });

  const selectedEntity = entities?.find(entity => entity.LogicalName === value);
  const displayValue = selectedEntity 
    ? selectedEntity.SchemaName || selectedEntity.LogicalName 
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          name={name}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || isLoading}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            (disabled || isLoading) && "opacity-70"
          )}
        >
          {isLoading ? (
            <div className="flex items-center">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading entities...
            </div>
          ) : (
            displayValue
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full min-w-[300px] p-0">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandInput
              placeholder="Search entities..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="h-9 border-none focus:ring-0"
            />
          </div>
          <CommandEmpty>No entities found.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-y-auto">
            {filteredEntities && filteredEntities.length > 0 ? (
              filteredEntities.map((entity) => (
                <CommandItem
                  key={entity.LogicalName}
                  value={entity.LogicalName}
                  onSelect={handleSelect}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === entity.LogicalName ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {entity.SchemaName || entity.LogicalName}
                </CommandItem>
              ))
            ) : (
              <CommandItem disabled className="cursor-default">
                {isLoading ? (
                  <div className="flex items-center justify-center w-full py-2">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading entities...
                  </div>
                ) : (
                  "No entities found"
                )}
              </CommandItem>
            )}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
});